import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  ButtonGroup,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TextHighlight from './TextHighlight';
import Navigation from './Navigation';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CommentIcon from '@mui/icons-material/Comment';

interface ZendeskTicket {
  id: number;
  external_id: string | null;
  subject: string;
  description: string;
  status: string;
  priority: string | null;
  type: string;
  created_at: string;
  updated_at: string;
  submitter_name: string | null;
  requester_name: string | null;
  assignee_name: string | null;
  classification: string | null;
  secondary_classification: string | null;
  redact: boolean;
  tags: string[];
  comments: ZendeskComment[];
  is_matching: boolean;
}

interface ZendeskComment {
  id: number;
  ticket_id: number;
  author_name: string | null;
  body: string;
  html_body: string;
  plain_body: string;
  public: boolean;
  created_at: string;
  classification: string | null;
  secondary_classification: string | null;
  redact: boolean;
  is_matching: boolean;
}

interface Filters {
  keyword: string;
  status: string;
  priority: string;
  requester: string;
  assignee: string;
  startDate: Date | null;
  endDate: Date | null;
  classification: string;
  redact: string;
}

const ZendeskView: React.FC = () => {
  const [tickets, setTickets] = useState<ZendeskTicket[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [priorities, setPriorities] = useState<string[]>([]);
  const [requesters, setRequesters] = useState<string[]>([]);
  const [assignees, setAssignees] = useState<string[]>([]);
  const [classifications] = useState([
    { value: 'produce', label: 'Produce' },
    { value: 'do_not_produce', label: 'Do Not Produce' },
    { value: 'attorney_client_privilege', label: 'Attorney-Client Privilege' }
  ]);
  const [filters, setFilters] = useState<Filters>({
    keyword: '',
    status: '',
    priority: '',
    requester: '',
    assignee: '',
    startDate: null,
    endDate: null,
    classification: '',
    redact: '',
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const [openFiltersModal, setOpenFiltersModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStatuses();
    fetchPriorities();
    fetchRequesters();
    fetchAssignees();
  }, []);

  useEffect(() => {
    fetchTickets(page);
  }, [page]);

  const fetchStatuses = async () => {
    try {
      const response = await fetch('/api/zendesk/statuses', {
        credentials: 'include',
      });
      const data = await response.json();
      setStatuses(data.statuses);
    } catch (error) {
      console.error('Error fetching statuses:', error);
    }
  };

  const fetchPriorities = async () => {
    try {
      const response = await fetch('/api/zendesk/priorities', {
        credentials: 'include',
      });
      const data = await response.json();
      setPriorities(data.priorities);
    } catch (error) {
      console.error('Error fetching priorities:', error);
    }
  };

  const fetchRequesters = async () => {
    try {
      const response = await fetch('/api/zendesk/requesters', {
        credentials: 'include',
      });
      const data = await response.json();
      setRequesters(data.requesters);
    } catch (error) {
      console.error('Error fetching requesters:', error);
    }
  };

  const fetchAssignees = async () => {
    try {
      const response = await fetch('/api/zendesk/assignees', {
        credentials: 'include',
      });
      const data = await response.json();
      setAssignees(data.assignees);
    } catch (error) {
      console.error('Error fetching assignees:', error);
    }
  };

  const fetchTickets = async (currentPage: number) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '10',
        ...(filters.keyword && { keyword: filters.keyword }),
        ...(filters.status && { status: filters.status }),
        ...(filters.priority && { priority: filters.priority }),
        ...(filters.requester && { requester: filters.requester }),
        ...(filters.assignee && { assignee: filters.assignee }),
        ...(filters.startDate && { start_date: filters.startDate.toISOString() }),
        ...(filters.endDate && { end_date: filters.endDate.toISOString() }),
        ...(filters.classification && { classification: filters.classification }),
        ...(filters.redact && { redact: filters.redact }),
      });

      const response = await fetch(
        `/api/zendesk/tickets?${queryParams}`,
        {
          credentials: 'include',
        }
      );
      const data = await response.json();
      setTickets(data.tickets);
      setTotalPages(data.pages);
      setTotalTickets(data.total);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTicketClassification = async (ticketId: number, classification: string) => {
    try {
      const response = await fetch(`/api/zendesk/tickets/${ticketId}/classify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ classification }),
      });

      if (response.ok) {
        setTickets(tickets.map(ticket =>
          ticket.id === ticketId ? { ...ticket, classification } : ticket
        ));
      }
    } catch (error) {
      console.error('Error classifying ticket:', error);
    }
  };

  const handleCommentClassification = async (commentId: number, classification: string) => {
    try {
      const response = await fetch(`/api/zendesk/comments/${commentId}/classify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ classification }),
      });

      if (response.ok) {
        setTickets(tickets.map(ticket => ({
          ...ticket,
          comments: ticket.comments.map(comment =>
            comment.id === commentId ? { ...comment, classification } : comment
          )
        })));
      }
    } catch (error) {
      console.error('Error classifying comment:', error);
    }
  };

  const handleTicketRedact = async (ticketId: number, redact: boolean) => {
    try {
      const response = await fetch(`/api/zendesk/tickets/${ticketId}/classify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ redact }),
      });

      if (response.ok) {
        setTickets(tickets.map(ticket =>
          ticket.id === ticketId ? { ...ticket, redact } : ticket
        ));
      }
    } catch (error) {
      console.error('Error updating ticket redact:', error);
    }
  };

  const handleCommentRedact = async (commentId: number, redact: boolean) => {
    try {
      const response = await fetch(`/api/zendesk/comments/${commentId}/classify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ redact }),
      });

      if (response.ok) {
        setTickets(tickets.map(ticket => ({
          ...ticket,
          comments: ticket.comments.map(comment =>
            comment.id === commentId ? { ...comment, redact } : comment
          )
        })));
      }
    } catch (error) {
      console.error('Error updating comment redact:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'solved':
      case 'closed':
        return 'success';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'normal':
        return 'primary';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const getClassificationColor = (classification: string | null) => {
    switch (classification) {
      case 'produce':
        return 'success';
      case 'do_not_produce':
        return 'error';
      case 'attorney_client_privilege':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleDownload = async () => {
    try {
      const queryParams = new URLSearchParams({
        ...(filters.keyword && { keyword: filters.keyword }),
        ...(filters.status && { status: filters.status }),
        ...(filters.priority && { priority: filters.priority }),
        ...(filters.requester && { requester: filters.requester }),
        ...(filters.assignee && { assignee: filters.assignee }),
        ...(filters.startDate && { start_date: filters.startDate.toISOString() }),
        ...(filters.endDate && { end_date: filters.endDate.toISOString() }),
        ...(filters.classification && { classification: filters.classification }),
        ...(filters.redact && { redact: filters.redact }),
      });

      const response = await fetch(
        `/api/zendesk/tickets/download?${queryParams}`,
        {
          credentials: 'include',
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to download tickets');
      }

      const data = await response.json();
      
      // Create and download the file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      a.download = `zendesk-tickets-${date}.json`;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading tickets:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Navigation */}
      <Navigation />
      
      {/* Main Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={6}>
            <Typography variant="h4" color="primary">
              Zendesk Ticket Viewer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Search and review support tickets with comments and context
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setOpenFiltersModal(true)}
              fullWidth
              sx={{ height: '56px' }}
            >
              Search & Filters
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
              fullWidth
              sx={{ height: '56px' }}
              disabled={!filters.keyword}
            >
              Export Results
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Filters Modal */}
      <Dialog 
        open={openFiltersModal} 
        onClose={() => setOpenFiltersModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Search & Filters</Typography>
          <IconButton 
            onClick={() => setOpenFiltersModal(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords (one per line)"
                value={filters.keyword}
                onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                multiline
                rows={4}
                required
                helperText="Enter search keywords, one per line. Tickets and comments matching any keyword will be included."
                placeholder="Enter keywords...&#10;e.g., account issue&#10;billing problem&#10;password reset"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Additional Filters
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filters.priority}
                  label="Priority"
                  onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                >
                  <MenuItem value="">All Priorities</MenuItem>
                  {priorities.map((priority) => (
                    <MenuItem key={priority} value={priority}>
                      {priority}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Requester</InputLabel>
                <Select
                  value={filters.requester}
                  label="Requester"
                  onChange={(e) => setFilters({ ...filters, requester: e.target.value })}
                >
                  <MenuItem value="">All Requesters</MenuItem>
                  {requesters.map((requester) => (
                    <MenuItem key={requester} value={requester}>
                      {requester}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Assignee</InputLabel>
                <Select
                  value={filters.assignee}
                  label="Assignee"
                  onChange={(e) => setFilters({ ...filters, assignee: e.target.value })}
                >
                  <MenuItem value="">All Assignees</MenuItem>
                  {assignees.map((assignee) => (
                    <MenuItem key={assignee} value={assignee}>
                      {assignee}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Classification</InputLabel>
                <Select
                  value={filters.classification}
                  label="Classification"
                  onChange={(e) => setFilters({ ...filters, classification: e.target.value })}
                >
                  <MenuItem value="">All Classifications</MenuItem>
                  {classifications.map((classification) => (
                    <MenuItem key={classification.value} value={classification.value}>
                      {classification.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Redaction Status</InputLabel>
                <Select
                  value={filters.redact}
                  label="Redaction Status"
                  onChange={(e) => setFilters({ ...filters, redact: e.target.value })}
                >
                  <MenuItem value="">All Items</MenuItem>
                  <MenuItem value="true">Redacted Only</MenuItem>
                  <MenuItem value="false">Non-Redacted Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Date Range
              </Typography>
            </Grid>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => setFilters({ ...filters, startDate: date })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => setFilters({ ...filters, endDate: date })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </LocalizationProvider>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setFilters({
                keyword: '',
                status: '',
                priority: '',
                requester: '',
                assignee: '',
                startDate: null,
                endDate: null,
                classification: '',
                redact: '',
              });
            }}
            color="secondary"
          >
            Clear All
          </Button>
          <Button
            onClick={() => {
              setOpenFiltersModal(false);
              setPage(1);
              fetchTickets(1);
            }}
            variant="contained"
            disabled={!filters.keyword.trim() || loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </DialogActions>
      </Dialog>

      {!filters.keyword && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Click "Search & Filters" to enter keywords and search tickets with comments.
        </Alert>
      )}

      {loading && (
        <Paper sx={{ p: 4, mb: 2, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Searching tickets...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This may take a moment for large result sets
          </Typography>
        </Paper>
      )}

      {/* Results Summary */}
      {!loading && tickets.length > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Found {totalTickets} tickets matching your search criteria
        </Alert>
      )}

      {/* Tickets Display */}
      {!loading && tickets.map((ticket) => (
        <Paper key={ticket.id} sx={{ mb: 3, backgroundColor: '#f9f9f9' }}>
          <Card>
            <CardContent>
              {/* Ticket Header */}
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ConfirmationNumberIcon color="primary" />
                      <Typography variant="h6" color="primary">
                        Ticket #{ticket.id}
                        {ticket.is_matching && (
                          <Chip 
                            label="MATCH" 
                            size="small" 
                            color="primary" 
                            sx={{ ml: 1 }} 
                          />
                        )}
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                      <TextHighlight text={ticket.subject} highlight={filters.keyword} />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Chip 
                        label={ticket.status} 
                        color={getStatusColor(ticket.status)} 
                        size="small" 
                      />
                      {ticket.priority && (
                        <Chip 
                          label={ticket.priority} 
                          color={getPriorityColor(ticket.priority)} 
                          size="small" 
                        />
                      )}
                      {ticket.classification && (
                        <Chip 
                          label={ticket.classification.replace('_', ' ')} 
                          color={getClassificationColor(ticket.classification)} 
                          size="small" 
                        />
                      )}
                      {ticket.redact && (
                        <Chip 
                          label="REDACTED" 
                          color="error" 
                          size="small" 
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Ticket Metadata */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Created: {new Date(ticket.created_at).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Requester: {ticket.requester_name || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Assignee: {ticket.assignee_name || 'Unassigned'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Comments: {ticket.comments.length}
                  </Typography>
                </Grid>
              </Grid>

              {/* Ticket Description */}
              {ticket.description && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <TextHighlight text={ticket.description} highlight={filters.keyword} />
                  </Typography>
                </Box>
              )}

              {/* Tags */}
              {ticket.tags.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Tags:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {ticket.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Ticket Classification Buttons */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Ticket Classification:
                </Typography>
                <ButtonGroup variant="contained" size="small" sx={{ mb: 1, mr: 2 }}>
                  <Button
                    color={ticket.classification === 'produce' ? 'success' : 'inherit'}
                    onClick={() => handleTicketClassification(ticket.id, 'produce')}
                  >
                    Produce
                  </Button>
                  <Button
                    color={ticket.classification === 'do_not_produce' ? 'error' : 'inherit'}
                    onClick={() => handleTicketClassification(ticket.id, 'do_not_produce')}
                  >
                    Do Not Produce
                  </Button>
                  <Button
                    color={ticket.classification === 'attorney_client_privilege' ? 'warning' : 'inherit'}
                    onClick={() => handleTicketClassification(ticket.id, 'attorney_client_privilege')}
                  >
                    Attorney-Client Privilege
                  </Button>
                </ButtonGroup>
                <ButtonGroup variant="outlined" size="small">
                  <Button
                    color={ticket.redact ? 'error' : 'inherit'}
                    onClick={() => handleTicketRedact(ticket.id, !ticket.redact)}
                  >
                    {ticket.redact ? 'Unredact' : 'Redact'}
                  </Button>
                </ButtonGroup>
              </Box>

              {/* Comments Section */}
              {ticket.comments.length > 0 && (
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`comments-${ticket.id}`}
                    id={`comments-header-${ticket.id}`}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CommentIcon />
                      <Typography variant="h6">
                        Comments ({ticket.comments.length})
                      </Typography>
                      {ticket.comments.some(c => c.is_matching) && (
                        <Chip label="HAS MATCHES" size="small" color="primary" />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {ticket.comments.map((comment) => (
                      <Paper 
                        key={comment.id} 
                        sx={{ 
                          p: 2, 
                          mb: 2, 
                          backgroundColor: 'white',
                          border: comment.is_matching ? '2px solid' : '1px solid #e0e0e0',
                          borderColor: comment.is_matching ? 'primary.main' : '#e0e0e0',
                          boxShadow: comment.is_matching ? '0 0 8px rgba(46, 125, 50, 0.3)' : 'none'
                        }}
                      >
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            <strong>{comment.author_name || 'Unknown Author'}</strong> • 
                            {new Date(comment.created_at).toLocaleString()} • 
                            {comment.public ? 'Public' : 'Private'}
                            {comment.is_matching && (
                              <Typography component="span" variant="body2" color="primary" sx={{ ml: 1, fontWeight: 'bold' }}>
                                • MATCH
                              </Typography>
                            )}
                            {comment.classification && (
                              <Chip 
                                label={comment.classification.replace('_', ' ')} 
                                color={getClassificationColor(comment.classification)} 
                                size="small" 
                                sx={{ ml: 1 }}
                              />
                            )}
                            {comment.redact && (
                              <Chip 
                                label="REDACTED" 
                                color="error" 
                                size="small" 
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          <TextHighlight text={comment.body} highlight={filters.keyword} />
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Comment Classification:
                          </Typography>
                          <ButtonGroup variant="contained" size="small" sx={{ mb: 1, mr: 2 }}>
                            <Button
                              color={comment.classification === 'produce' ? 'success' : 'inherit'}
                              onClick={() => handleCommentClassification(comment.id, 'produce')}
                            >
                              Produce
                            </Button>
                            <Button
                              color={comment.classification === 'do_not_produce' ? 'error' : 'inherit'}
                              onClick={() => handleCommentClassification(comment.id, 'do_not_produce')}
                            >
                              Do Not Produce
                            </Button>
                            <Button
                              color={comment.classification === 'attorney_client_privilege' ? 'warning' : 'inherit'}
                              onClick={() => handleCommentClassification(comment.id, 'attorney_client_privilege')}
                            >
                              Attorney-Client Privilege
                            </Button>
                          </ButtonGroup>
                          <ButtonGroup variant="outlined" size="small">
                            <Button
                              color={comment.redact ? 'error' : 'inherit'}
                              onClick={() => handleCommentRedact(comment.id, !comment.redact)}
                            >
                              {comment.redact ? 'Unredact' : 'Redact'}
                            </Button>
                          </ButtonGroup>
                        </Box>
                      </Paper>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}
            </CardContent>
          </Card>
        </Paper>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default ZendeskView; 